class MegaMenuKeyboardNav {
  constructor() {
    this.currentMenuIndex = 0;
    this.isMenuOpen = false;
    this.focusableElements = [];
    this.menuTriggers = [];
    this.lastFocusedTrigger = null;
    this.megaMenuContainer = null;
    this.init();
  }

  init() {
    // Set up event listeners
    this.setupMenuTriggers();
    this.setupKeyboardHandlers();
    this.makeImageLinksTabable();
  }

  setupMenuTriggers() {
    this.menuTriggers = document.querySelectorAll('[data-hook="mega-link"]');
    
    this.menuTriggers.forEach((trigger, index) => {
      const link = trigger.querySelector('a, button');
      if (link) {
        // Make menu openable with keyboard
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            // Enter should navigate to the link URL (normal link behavior)
            const targetId = trigger.getAttribute('data-target');
            const megaMenu = document.querySelector(targetId);
            
            if (megaMenu) {
              // If this has a mega menu, prevent default and navigate manually
              e.preventDefault();
              window.location.href = link.href;
            }
            // If no mega menu, allow normal link behavior
          }
          if (e.code === 'Space' || e.key === ' ') {
            // Space should open the mega menu
            e.preventDefault();
            this.openMenu(trigger, index);
          }
        });

        // Prevent click navigation when menu should open instead
        link.addEventListener('click', (e) => {
          const targetId = trigger.getAttribute('data-target');
          const megaMenu = document.querySelector(targetId);
          
          // If this trigger has a mega menu, allow normal link navigation on click
          if (megaMenu) {
            // Allow the click to navigate to the link URL
            // Don't prevent default - let it work normally
            // console.log('Click on mega menu trigger - allowing navigation to:', link.href);
          }
          // If no mega menu, allow normal link behavior
        });

        // Also handle mouse hover for consistency
        trigger.addEventListener('mouseenter', () => {
          this.openMenu(trigger, index);
        });
      }
    });

    // Close menu on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.closeMenu();
      }
    });
  }

  openMenu(trigger, index) {
    // console.log('Opening menu for trigger:', trigger);
    this.currentMenuIndex = index;
    this.isMenuOpen = true;
    
    // Store which trigger opened this menu
    this.lastFocusedTrigger = trigger.querySelector('a, button');
    // console.log('Stored lastFocusedTrigger:', this.lastFocusedTrigger);

    // Hide all other menus first - make sure ALL sections are properly hidden
    const allMenus = document.querySelectorAll('[id^="mega-feature--"]');
    allMenus.forEach(menu => {
      menu.classList.remove('on');
      menu.classList.add('off');
      menu.setAttribute('aria-hidden', 'true');
    });

    // Get the target mega menu
    const targetId = trigger.getAttribute('data-target');
    const megaMenu = document.querySelector(targetId);
    this.megaMenuContainer = megaMenu;
    
    if (megaMenu) {
      // Show the menu - this makes the overall container visible
      megaMenu.classList.add('on');
      megaMenu.classList.remove('off');
      megaMenu.setAttribute('aria-hidden', 'false');
      
      // Update trigger aria-expanded
      const triggerLink = trigger.querySelector('a, button');
      if (triggerLink) {
        triggerLink.setAttribute('aria-expanded', 'true');
      }
      
      // Get all focusable elements in the mega menu
      this.focusableElements = this.getFocusableElements(megaMenu);
      // console.log('Focusable elements in mega menu:', this.focusableElements);
      
      // Set up focus/blur handlers for the mega menu elements
      this.setupMegaMenuFocusHandlers(megaMenu);
      
      // IMPORTANT: Also ensure the first mega-feature section is visible by default
      // This mimics what happens on hover
      const firstMegaFeatureSection = megaMenu.querySelector('[id^="mega-feature--"]');
      if (firstMegaFeatureSection) {
        firstMegaFeatureSection.classList.add('on');
        firstMegaFeatureSection.classList.remove('off');
        firstMegaFeatureSection.setAttribute('aria-hidden', 'false');
        // console.log('Made first mega-feature section visible:', firstMegaFeatureSection);
      }
      
      // Focus the first focusable element in the mega menu - ONLY for keyboard navigation
      setTimeout(() => {
        const visible = this.focusableElements.filter(el => el.offsetParent !== null);
        if (visible.length > 0 && this.lastFocusedTrigger === document.activeElement) {
          // Only auto-focus if the trigger is still focused (keyboard navigation)
          // If user clicked, the focus would have moved away already
          visible[0].focus();
          // console.log('Auto-focused first element for keyboard navigation:', visible[0]);
        }
      }, 100); // Small delay to ensure menu is visible
    }
  }

  setupMegaMenuFocusHandlers(megaMenu) {
    // Find all image links in the mega menu
    const imageLinks = megaMenu.querySelectorAll('.mega-feature a');
    
    imageLinks.forEach(link => {
      // Add focus handler that shows outline for keyboard navigation
      link.addEventListener('focus', (e) => {
        // console.log('Focus event triggered on:', link);
        
        // Check if this is keyboard focus - when in mega menu, it's always keyboard focus
        const isKeyboardFocus = this.isMenuOpen || e.target.matches(':focus-visible');
        
        // console.log('Is keyboard focus:', isKeyboardFocus, 'Menu open:', this.isMenuOpen);
        
        if (isKeyboardFocus) {
          // FIRST: Apply the outline BEFORE any scaling to prevent clipping
          const focusStyles = `
            outline: 3px solid #3b82f6 !important;
            outline-offset: 8px !important;
            border-radius: 8px !important;
            box-shadow: 0 0 0 2px #3b82f6, 0 0 0 10px rgba(255,255,255,0.8) !important;
            position: relative !important;
            z-index: 999 !important;
            overflow: visible !important;
          `;
          
          // Apply outline styles first
          link.style.cssText += focusStyles;
          link.classList.add('force-focus-outline');
          link.setAttribute('data-keyboard-focused', 'true');
          
          // console.log('Applied focus outline with larger offset:', link);
        }
        
        // ALWAYS handle the mega-feature section visibility
        const megaFeatureSection = link.closest('[id^="mega-feature--"]');
        if (megaFeatureSection) {
          // Hide all other mega-feature sections first
          const allMegaFeatureSections = document.querySelectorAll('[id^="mega-feature--"]');
          allMegaFeatureSections.forEach(section => {
            section.classList.remove('on');
            section.classList.add('off');
            section.setAttribute('aria-hidden', 'true');
          });
          
          // Now show this specific section
          megaFeatureSection.classList.add('on');
          megaFeatureSection.classList.remove('off');
          megaFeatureSection.setAttribute('aria-hidden', 'false');
        }
        
        // THEN: Apply scaling effects with reduced scale to minimize clipping
        link.classList.add('group-focus-active');
        
        // Find the image and span within the link and apply hover effects
        const image = link.querySelector('img');
        const span = link.querySelector('span');
        
        if (image) {
          // Use a smaller scale to prevent outline clipping
          image.style.transform = 'scale(1.02)';
          image.style.transformOrigin = 'center center';
          // console.log('Applied reduced scale to prevent outline clipping');
        }
        
        if (span) {
          span.style.backgroundColor = '#2563eb';
          span.style.color = 'white';
          span.style.transform = 'scale(1.02)'; // Also reduce span scaling
        }
      });
      
      // Add blur handler to remove all focus effects
      link.addEventListener('blur', (e) => {
        link.classList.remove('focus-visible');
        link.classList.remove('force-focus-outline');
        link.classList.remove('keyboard-focused');
        link.classList.remove('mega-menu-focused');
        link.classList.remove('group-focus-active');
        link.removeAttribute('data-keyboard-focused');
        
        // Remove all the forced outline styles
        const stylesToRemove = [
          'outline', 'outline-offset', 'border-radius', 'box-shadow', 
          'position', 'z-index', '--focus-outline', '--focus-outline-offset'
        ];
        stylesToRemove.forEach(prop => {
          link.style.removeProperty(prop);
        });
        
        // Remove the hover effect styles
        const image = link.querySelector('img');
        const span = link.querySelector('span');
        
        if (image) {
          image.style.transform = '';
        }
        
        if (span) {
          span.style.backgroundColor = '';
          span.style.color = '';
          span.style.transform = '';
        }
        
        // console.log('All focus effects removed from image link:', link);
      });
    });
  }

  closeMenu() {
    this.isMenuOpen = false;
    this.megaMenuContainer = null;
    
    // Hide all mega menus
    const allMenus = document.querySelectorAll('[id^="mega-feature--"]');
    allMenus.forEach(menu => {
      menu.classList.remove('on');
      menu.classList.add('off');
      menu.setAttribute('aria-hidden', 'true');
    });

    // Update all trigger aria-expanded states
    const allTriggers = document.querySelectorAll('[data-hook="mega-link"] a');
    allTriggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    // Return focus to the last focused trigger
    if (this.lastFocusedTrigger) {
      // console.log('Returning focus to:', this.lastFocusedTrigger);
      this.lastFocusedTrigger.focus();
    }
  }

  getFocusableElements(container) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors));
  }

  setupKeyboardHandlers() {
    // Use a more specific approach - listen on the document but check if we're in our mega menu
    document.addEventListener('keydown', (e) => {
      // console.log('Keydown event:', e.key, 'Menu open:', this.isMenuOpen);
      
      if (!this.isMenuOpen) return;

      switch(e.key) {
        case 'Tab':
          // console.log('Tab pressed, shift:', e.shiftKey);
          this.handleTabNavigation(e);
          break;
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          this.handleArrowNavigation(e.key);
          break;
        case 'Home':
          e.preventDefault();
          this.focusFirstElement();
          break;
        case 'End':
          e.preventDefault();
          this.focusLastElement();
          break;
      }
    });
  }

  handleTabNavigation(e) {
    if (!this.isMenuOpen || !this.megaMenuContainer) {
      // console.log('Menu not open or no container');
      return;
    }

    const currentFocus = document.activeElement;
    // console.log('Current focus:', currentFocus);
    
    // Check if current focus is in the mega menu
    const isInMegaMenu = this.megaMenuContainer.contains(currentFocus);
    // console.log('Is in mega menu:', isInMegaMenu);
    
    // Get visible focusable elements in the mega menu
    const visibleMegaMenuElements = this.focusableElements.filter(el => {
      return el.offsetParent !== null && !el.disabled;
    });
    
    if (e.shiftKey && isInMegaMenu) {
      // Shift + Tab backwards from mega menu
      const currentIndex = visibleMegaMenuElements.indexOf(currentFocus);
      // console.log('Current index in mega menu:', currentIndex);
      
      if (currentIndex === 0) {
        // We're at the first element in mega menu, go back to the trigger
        e.preventDefault();
        // console.log('Going back to trigger:', this.lastFocusedTrigger);
        if (this.lastFocusedTrigger) {
          this.lastFocusedTrigger.focus();
        }
      }
      // Otherwise, let normal tab behavior happen within the mega menu
    } else if (!e.shiftKey && isInMegaMenu) {
      // Tab forward within mega menu
      const currentIndex = visibleMegaMenuElements.indexOf(currentFocus);
      
      if (currentIndex === visibleMegaMenuElements.length - 1) {
        // We're at the last element, wrap to first or exit
        e.preventDefault();
        if (visibleMegaMenuElements.length > 0) {
          visibleMegaMenuElements[0].focus();
        }
      }
    }
  }

  handleArrowNavigation(direction) {
    const currentFocus = document.activeElement;
    const currentIndex = this.focusableElements.indexOf(currentFocus);
    
    // Only handle arrow navigation within the mega menu
    if (currentIndex === -1) return;
    
    let nextIndex;
    if (direction === 'ArrowDown') {
      nextIndex = currentIndex + 1;
      if (nextIndex >= this.focusableElements.length) {
        nextIndex = 0;
      }
    } else {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        nextIndex = this.focusableElements.length - 1;
      }
    }
    
    this.focusableElements[nextIndex].focus();
  }

  focusFirstElement() {
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  focusLastElement() {
    if (this.focusableElements.length > 0) {
      this.focusableElements[this.focusableElements.length - 1].focus();
    }
  }

  makeImageLinksTabable() {
    // Ensure all image links are properly focusable
    const imageLinks = document.querySelectorAll('.mega-feature a, [data-utility="link-to"]');
    
    imageLinks.forEach(link => {
      // Ensure tabindex is set (but not for the main container)
      if (!link.closest('[data-utility="link-to"]') || link.matches('.mega-feature a')) {
        if (!link.hasAttribute('tabindex')) {
          link.setAttribute('tabindex', '0');
        }
      }
      
      // Track keyboard vs mouse navigation
      let keyboardFocus = false;
      
      // Add keyboard event handlers
      link.addEventListener('keydown', (e) => {
        keyboardFocus = true;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          link.click();
        }
      });
      
      // Track mouse events
      link.addEventListener('mousedown', () => {
        keyboardFocus = false;
      });
      
      // Add focus/blur handlers for keyboard-only focus styling
      link.addEventListener('focus', () => {
        if (keyboardFocus) {
          // Use setProperty with !important to ensure visibility for keyboard focus only
          link.style.cssText += `
            outline: 3px solid #3b82f6 !important;
            outline-offset: 4px !important;
            border-radius: 8px !important;
            box-shadow: 0 0 0 2px #3b82f6, 0 0 0 6px white !important;
            position: relative !important;
            z-index: 999 !important;
          `;
          link.classList.add('force-focus-outline');
          // console.log('Keyboard focus applied via makeImageLinksTabable:', link);
        }
      });
      
      link.addEventListener('blur', () => {
        link.classList.remove('force-focus-outline');
        const stylesToRemove = ['outline', 'outline-offset', 'border-radius', 'box-shadow', 'position', 'z-index'];
        stylesToRemove.forEach(prop => {
          link.style.removeProperty(prop);
        });
        keyboardFocus = false;
        // console.log('Focus removed via makeImageLinksTabable:', link);
      });
    });
  }
}

// Initialize when DOM is ready
function initializeMegaMenu() {
  // console.log('Initializing MegaMenuKeyboardNav');
  window.megaMenuNav = new MegaMenuKeyboardNav();
}

document.addEventListener('DOMContentLoaded', initializeMegaMenu);

// Also handle if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMegaMenu);
} else {
  initializeMegaMenu();
}

// Remove unnecessary aria-controls attributes from navigation menu links for accessibility compliance
function removeAriaControls() {
  // Target only navigation menu links with hover-toggle functionality
  const navMenuLinks = document.querySelectorAll('nav a[data-utility="hover-toggle"]');
  
  navMenuLinks.forEach(link => {
    if (link.hasAttribute('aria-controls')) {
      link.removeAttribute('aria-controls');
    }
  });
}

// Run aria-controls removal
removeAriaControls();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', removeAriaControls);
} else {
  removeAriaControls();
}

// Run after other scripts have likely loaded
setTimeout(removeAriaControls, 0);
setTimeout(removeAriaControls, 10);
setTimeout(removeAriaControls, 100);
setTimeout(removeAriaControls, 500);
setTimeout(removeAriaControls, 1000);

// Watch for any future additions using MutationObserver
const mmObserver = new MutationObserver(function(mutations) {
  mutations.forEach(function(mutation) {
    if (mutation.type === 'attributes' && mutation.attributeName === 'aria-controls') {
      const target = mutation.target;
      
      if (target.matches('nav a[data-utility="hover-toggle"]')) {
        target.removeAttribute('aria-controls');
      }
    }
  });
});

// Start observing immediately
mmObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['aria-controls'],
  subtree: true
});